import { ChartSettingsT, OrderedColumnsT } from 'business/definitions';
import { getOrderedColumns } from 'helpers/chartHelper';
import { UnformatedVizData, FormatedVizData } from './types';

export const orderColumnWithContext = (
    context: UnformatedVizData,
): FormatedVizData => {
    const {
        chartData,
        chartColumns,
        context: { settings, vizType },
    } = context;

    const newChartColumns = getOrderedColumns(
        chartData,
        chartColumns,
        vizType,
        settings.reverseGroupBy,
        settings.isYearlyComparison,
    );
    return {
        ...context,
        chartColumns: newChartColumns,
    };
};

/**
 * Checks if a given column is displyed on a given axis
 */
const isDimensionOnChartAxis = (
    column: ResponseColumnT,
    chartDimensionKey: keyof OrderedColumnsT,
) => column.displayOnAxis === chartDimensionKey;

/**
 * If the column does not have the displayOnAxis property, we check if it is
 * present in the chart conception settings
 */
const isDimensionOnChartConceptionAxis = (
    column: ResponseColumnT,
    chartDimensionKey: keyof OrderedColumnsT,
    chartConception: ChartSettingsT['chartConception'],
) =>
    chartConception
        ?.filter((col) => col.chartDimensionKey === chartDimensionKey)
        .map((col) => col.id)
        .includes(column.key);
/**
 * This function is used to order the dimensions of the chart according to the
 * chart conception settings.
 * It makes some hypothesis:
 * - the context contains a maximum of 3 columns to be displayed on the chart
 * - the x and z dimensions behaviour is similar: they are tagged using the displayOnAxis property
 * or they are raw (i.e. unmerged) and we can get them using the chartConception settings
 * - the y dimension is the remaining one
 */
export const conceptionChartDimensionOrderingWithContext = (
    context: UnformatedVizData,
): FormatedVizData => {
    const {
        chartColumns,
        context: {
            settings: { chartConception },
        },
    } = context;
    const xDimension = chartColumns.find(
        (column) =>
            isDimensionOnChartAxis(column, 'x') ||
            isDimensionOnChartConceptionAxis(column, 'x', chartConception),
    );
    const zDimension = chartColumns.find(
        (column) =>
            isDimensionOnChartAxis(column, 'z') ||
            isDimensionOnChartConceptionAxis(column, 'z', chartConception),
    );

    const yDimension = chartColumns.find(
        (column) =>
            column.key !== xDimension?.key && column.key !== zDimension?.key,
    );
    if (!yDimension) {
        throw new Error('The y dimension cannot be undefined');
    }
    return {
        ...context,
        chartColumns: {
            x: xDimension,
            y: yDimension,
            z: zDimension,
        },
    };
};
